import { downloadDailyFeeReportPerFeeHeadPDF } from "./DailyFeeReportPerFeeHeadPDF";
// Use API_BASE_URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Utility to fetch college details (copied from DailyFeeReport)
async function fetchCollegeDetails(clientId, collegeId) {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const res = await axios.get(`${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`);
    if (res.data && res.data.success) {
      return res.data.data;
    }
  } catch (e) {
    console.error("Failed to fetch college details", e);
  }
  return null;
}

// Utility to convert image URL to base64 (copied from DailyFeeReport)
function toBase64(url) {
  return fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Snackbar,
  Alert,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";

export default function DailyFeeReportPerFeeHead() {
  const [collegeDetails, setCollegeDetails] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const collegeId = user?.college_id;
  const clientId = user?.client_id;
  const handleGeneratePDF = async () => {
    if (!rows.length) {
      setSnackbar({ open: true, message: "No data to generate PDF.", severity: "warning" });
      return;
    }
    const feeTypeObj = feeTypes.find((t) => t.fee_type_id === feeTypeId) || {};
    const feeType = feeTypeObj.fee_type_name || "";
    const examType = feeTypeObj.exam_type_name || "";
    const feeHead = feeHeads.find((h) => h.fee_head_id === feeHeadId)?.fee_head_name || "";
    const paymentStatusLabel =
      paymentStatus === "all" ? "All" : paymentStatus === "paid" ? "Paid" : "Not Paid";
    await downloadDailyFeeReportPerFeeHeadPDF({
      rows,
      columns,
      fromDate,
      toDate,
      academicYear,
      feeType,
      feeHead,
      examType,
      collegeDetails,
      logoBase64,
      paymentStatus: paymentStatusLabel,
    });
  };

  // Handler for downloading data as Excel (same format as PDF)
  const handleDownloadExcel = () => {
    if (!rows.length) {
      setSnackbar({ open: true, message: "No data to export.", severity: "warning" });
      return;
    }

    // Group rows by receipt_date (same as PDF)
    const grouped = {};
    let grandTotal = 0;

    rows.forEach((row) => {
      const date = row.receipt_date ? new Date(row.receipt_date).toLocaleDateString() : "";
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(row);
      let amtStr = (row.paid_amount ?? "").toString().replace(/[\,\s]/g, "");
      const amt = parseFloat(amtStr) || 0;
      grandTotal += amt;
    });

    // Prepare Excel data with subtotals (same as PDF)
    const excelData = [];
    let serialNo = 1;

    // Add header row
    excelData.push([
      "S. No.",
      "Student ID",
      "Student Name",
      "Roll No",
      "Receipt Date",
      "Actual Amount (₹)",
      "Paid Amount (₹)",
      "Remarks",
      "Payment Status",
    ]);

    // Add data rows grouped by date
    Object.keys(grouped)
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA - dateB;
      })
      .forEach((date) => {
        let dateTotal = 0;
        grouped[date].forEach((row) => {
          let amtStr = (row.paid_amount ?? "").toString().replace(/[\,\s]/g, "");
          dateTotal += parseFloat(amtStr) || 0;
          const paymentStatusText =
            row.fee_status === "paid"
              ? "Paid"
              : row.fee_status === "notpaid"
              ? "Not Paid"
              : row.fee_status || "-";

          excelData.push([
            serialNo++,
            row.student_id || "",
            row.student_name || "",
            row.student_rollno || "",
            date,
            row.actual_amount ? Number(row.actual_amount).toLocaleString("en-IN") : "0.00",
            row.paid_amount ? Number(row.paid_amount).toLocaleString("en-IN") : "0.00",
            row.remarks || "",
            paymentStatusText,
          ]);
        });
        // Add subtotal row for this date
        excelData.push([
          "",
          "",
          "",
          "",
          `Total for ${date}`,
          "",
          `₹ ${dateTotal.toLocaleString("en-IN")}`,
          "",
          "",
        ]);
        // Add empty row for spacing
        excelData.push([]);
      });

    // Add grand total row
    excelData.push([
      "",
      "",
      "",
      "",
      "GRAND TOTAL",
      "",
      `₹ ${grandTotal.toLocaleString("en-IN")}`,
      "",
      "",
    ]);

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    ws["!cols"] = [
      { wch: 8 }, // S. No.
      { wch: 15 }, // Student ID
      { wch: 25 }, // Student Name
      { wch: 15 }, // Roll No
      { wch: 15 }, // Receipt Date
      { wch: 18 }, // Actual Amount
      { wch: 18 }, // Paid Amount
      { wch: 30 }, // Remarks
      { wch: 15 }, // Payment Status
    ];

    // Style the header row (make it bold)
    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, sz: 12 },
        fill: { fgColor: { rgb: "CCCCCC" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Style subtotal and grand total rows
    for (let R = 1; R <= excelData.length; R++) {
      const firstCellAddress = XLSX.utils.encode_cell({ r: R, c: 4 });
      if (
        ws[firstCellAddress] &&
        ws[firstCellAddress].v &&
        (String(ws[firstCellAddress].v).startsWith("Total for") ||
          String(ws[firstCellAddress].v) === "GRAND TOTAL")
      ) {
        for (let C = 0; C <= 8; C++) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[address]) {
            ws[address].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "E0E0E0" } },
            };
          }
        }
      }
    }

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily Fee Report");

    // Generate filename with date range
    const fileName = `Daily_Fee_Report_${fromDate}_to_${toDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fileName);

    setSnackbar({
      open: true,
      message: "Excel file downloaded successfully!",
      severity: "success",
    });
  };

  React.useEffect(() => {
    if (!clientId || !collegeId) return;
    fetchCollegeDetails(clientId, collegeId).then((college) => {
      setCollegeDetails(college);
      if (college && college.college_logopath && college.society_id && college.college_id) {
        const logoPath = `/uploads/logos/${clientId}/${college.society_id}/${college.college_id}/${college.college_logopath}`;
        toBase64(logoPath)
          .then((base64) => setLogoBase64(base64))
          .catch(() => setLogoBase64(null));
      } else {
        setLogoBase64(null);
      }
    });
  }, [clientId, collegeId]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [feeTypeId, setFeeTypeId] = useState("");
  const [feeHeadId, setFeeHeadId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [academicYears, setAcademicYears] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    if (!clientId || !collegeId) return;
    axios
      .get(
        `${API_BASE_URL}/api/dailyfeereportperfeehead/academicyears/${clientId}?college_id=${collegeId}`
      )
      .then((res) => setAcademicYears(res.data.data || []))
      .catch((error) => {
        console.error("Error fetching academic years:", error);
        setSnackbar({ open: true, message: "Failed to fetch academic years.", severity: "error" });
      });

    axios
      .get(
        `${API_BASE_URL}/api/dailyfeereportperfeehead/feetypes/${clientId}?college_id=${collegeId}`
      )
      .then((res) => setFeeTypes(res.data.data || []))
      .catch((error) => {
        console.error("Error fetching fee types:", error);
        setSnackbar({ open: true, message: "Failed to fetch fee types.", severity: "error" });
      });
  }, [clientId, collegeId]);

  useEffect(() => {
    if (academicYear && feeTypeId) {
      axios
        .get(
          `${API_BASE_URL}/api/dailyfeereportperfeehead/feeheads/${clientId}?academic_year=${academicYear}&fee_type_id=${feeTypeId}`
        )
        .then((res) => setFeeHeads(res.data.data || []))
        .catch((error) => {
          console.error("Error fetching fee heads:", error);
          setSnackbar({ open: true, message: "Failed to fetch fee heads.", severity: "error" });
        });
    } else {
      setFeeHeads([]);
    }
    setFeeHeadId("");
  }, [academicYear, feeTypeId, clientId]);

  const handleFetch = async () => {
    if (!fromDate || !toDate || !feeHeadId) {
      setSnackbar({
        open: true,
        message: "Please select from date, to date, and fee head.",
        severity: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/dailyfeereportperfeehead/${clientId}?from_date=${fromDate}&to_date=${toDate}&fee_head_id=${feeHeadId}`;
      if (paymentStatus && paymentStatus !== "all") {
        url += `&fee_status=${paymentStatus}`;
      }

      const res = await axios.get(url);
      setRows(res.data.data || []);
      if (res.data.data?.length === 0) {
        setSnackbar({
          open: true,
          message: "No data found for selected criteria.",
          severity: "info",
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to fetch report.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "serialNo",
      headerName: "S. No.",
      width: 80,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        return params.api ? params.api.getRowIndex(params.id) + 1 : "";
      },
    },
    { field: "student_id", headerName: "Student ID", flex: 1, minWidth: 120 },
    { field: "student_name", headerName: "Student Name", flex: 1, minWidth: 150 },
    { field: "student_rollno", headerName: "Roll No", flex: 1, minWidth: 100 },
    {
      field: "receipt_date",
      headerName: "Receipt Date",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params.value) return "";
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: "actual_amount",
      headerName: "Actual Amount",
      flex: 1,
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => {
        if (!params.value) return "₹0.00";
        return Number(params.value).toLocaleString("en-IN", { style: "currency", currency: "INR" });
      },
    },
    {
      field: "paid_amount",
      headerName: "Paid Amount",
      flex: 1,
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => {
        if (!params.value) return "₹0.00";
        return Number(params.value).toLocaleString("en-IN", { style: "currency", currency: "INR" });
      },
    },
    { field: "remarks", headerName: "Remarks", flex: 2, minWidth: 200 },
    {
      field: "fee_status",
      headerName: "Payment Status",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const status = params.value;
        let statusText = "";
        let statusColor = "";
        if (status === "paid") {
          statusText = "Paid";
          statusColor = "#4caf50";
        } else if (status === "notpaid") {
          statusText = "Not Paid";
          statusColor = "#f44336";
        } else {
          statusText = status || "Unknown";
          statusColor = "#ff9800";
        }
        return (
          <span
            style={{
              backgroundColor: statusColor,
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              display: "inline-block",
            }}
          >
            {statusText}
          </span>
        );
      },
    },
  ];

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Toolbar>
          <Typography variant="h6">Daily Fee Report Per Fee Head</Typography>
        </Toolbar>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            size="small"
          />
          <TextField
            select
            label="Academic Year"
            name="academic_year"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            required
            sx={{ minWidth: 140 }}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Academic Year</option>
            {academicYears.map((y) => (
              <option key={y.academic_year} value={y.academic_year}>
                {y.academic_year}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Fee Type"
            name="fee_type_id"
            value={feeTypeId}
            onChange={(e) => setFeeTypeId(e.target.value)}
            required
            sx={{ minWidth: 140 }}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Fee Type</option>
            {feeTypes.map((t) => (
              <option key={t.fee_type_id} value={t.fee_type_id}>
                {t.fee_type_name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Fee Head"
            name="fee_head_id"
            value={feeHeadId}
            onChange={(e) => setFeeHeadId(e.target.value)}
            required
            sx={{ minWidth: 180 }}
            disabled={!feeHeads.length}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Fee Head</option>
            {feeHeads.map((h) => (
              <option key={h.fee_head_id} value={h.fee_head_id}>
                {h.fee_head_name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Payment Status"
            name="payment_status"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
            sx={{ minWidth: 160 }}
            size="small"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="all">All (Paid & Not Paid)</option>
            <option value="paid">Paid Only</option>
            <option value="notpaid">Not Paid Only</option>
          </TextField>
          <Button
            variant="contained"
            onClick={handleFetch}
            disabled={loading}
            size="medium"
            sx={{ color: "#fff" }}
          >
            {loading ? <CircularProgress size={24} /> : "Fetch Report"}
          </Button>
          <Button
            variant="contained"
            size="medium"
            sx={{
              ml: 1,
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#222" },
            }}
            onClick={handleGeneratePDF}
          >
            DOWNLOAD PDF
          </Button>
          <Button
            variant="contained"
            size="medium"
            sx={{
              ml: 1,
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#222" },
            }}
            onClick={handleDownloadExcel}
          >
            DOWNLOAD EXCEL
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <DataGrid
          autoHeight
          rows={rows.map((r, i) => ({ id: i + 1, ...r }))}
          columns={columns}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordWrap: "break-word",
            },
          }}
        />
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Add prop types validation
DailyFeeReportPerFeeHead.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  collegeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
